/**
 * bookingDocuments.ts — FleetHub B2B document upload service (PLA-122)
 *
 * Client-side service for attaching booking documents after a FleetHub order
 * is created. Mirrors the callable-invocation pattern used throughout
 * app/services/firebase/functions.ts.
 *
 * Flow:
 *   1. Convert each File → base64 string (web FileReader or fetch().arrayBuffer() fallback)
 *   2. Report progress via optional callback
 *   3. Invoke attachBookingDocumentsB2b callable with { ticketId, files }
 *   4. Return { success, fileIds, noteId } on success
 *   5. Surface backend HttpsError codes as typed BookingDocumentError for the retry banner
 *
 * Web-only production target: FileReader.readAsDataURL is the primary conversion path.
 * Native fallback: expo-file-system is used when FileReader is unavailable.
 *
 * ADR-0022: Two-step book→attach with split auth.
 */

import { callableWithAuthRetry } from "./firebase/functions"

// ── Types ─────────────────────────────────────────────────────────────────────

/** Shape of a single file entry sent to the callable. */
interface BookingDocumentFilePayload {
  /** Raw base64-encoded file content (no data-URL prefix). */
  base64: string
  /** Original file name including extension, e.g. "fahrzeugschein.pdf". */
  fileName: string
  /** MIME type declared by the file, e.g. "application/pdf". */
  mimeType: string
}

interface AttachBookingDocumentsRequest {
  ticketId: string
  files: BookingDocumentFilePayload[]
}

interface AttachBookingDocumentsResponse {
  success: true
  fileIds: string[]
  noteId: string
}

/** Error code that the retry banner can inspect for a user-friendly message. */
export type BookingDocumentErrorCode =
  | "invalid-argument"
  | "permission-denied"
  | "unauthenticated"
  | "internal"
  | "unknown"

/** Typed error surfaced by uploadBookingDocumentsB2b on failure. */
export class BookingDocumentError extends Error {
  constructor(
    public readonly code: BookingDocumentErrorCode,
    public readonly userMessage: string,
  ) {
    super(userMessage)
    this.name = "BookingDocumentError"
  }
}

/** Result returned on successful upload. */
export interface UploadBookingDocumentsResult {
  success: true
  fileIds: string[]
  noteId: string
}

// ── Base64 conversion ─────────────────────────────────────────────────────────

/**
 * Convert a File object to a raw base64 string (no data-URL prefix).
 * Primary path: FileReader (web).
 * Fallback: fetch().arrayBuffer() for environments without FileReader.
 */
async function fileToBase64(file: File): Promise<string> {
  // Web path: FileReader is the standard browser API
  if (typeof FileReader !== "undefined") {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const dataUrl = reader.result as string
        // Strip "data:<mime>;base64," prefix
        const base64 = dataUrl.split(",")[1]
        if (!base64) {
          reject(new Error(`Failed to extract base64 from data URL for "${file.name}"`))
          return
        }
        resolve(base64)
      }
      reader.onerror = () => reject(new Error(`FileReader error for "${file.name}"`))
      reader.readAsDataURL(file)
    })
  }

  // Native / fallback path: fetch the file as an ArrayBuffer and encode manually
  const arrayBuffer = await file.arrayBuffer()
  const bytes = new Uint8Array(arrayBuffer)
  let binary = ""
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

// ── German error messages ─────────────────────────────────────────────────────

function toBookingDocumentError(error: unknown): BookingDocumentError {
  if (error instanceof BookingDocumentError) return error

  // Firebase callable throws objects with a .code and .message property
  const err = error as { code?: string; message?: string }
  const code = err?.code as BookingDocumentErrorCode | undefined

  switch (code) {
    case "invalid-argument":
      return new BookingDocumentError(
        "invalid-argument",
        err.message || "Eine oder mehrere Dateien sind ungültig. Bitte prüfe Dateityp und -größe.",
      )
    case "permission-denied":
      return new BookingDocumentError(
        "permission-denied",
        "Dokumente konnten nicht hochgeladen werden: fehlende Berechtigung.",
      )
    case "unauthenticated":
      return new BookingDocumentError(
        "unauthenticated",
        "Bitte melde dich erneut an und versuche es noch einmal.",
      )
    default:
      return new BookingDocumentError(
        "internal",
        "Dokumente konnten nicht hochgeladen werden. Bitte erneut versuchen.",
      )
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Upload booking documents for a B2B FleetHub order.
 *
 * @param orderId    - HubSpot ticket ID returned by createFleetHubOrder
 * @param files      - Array of File objects selected by the user (max 3)
 * @param onProgress - Optional callback fired after each file is converted (0–1)
 * @throws {BookingDocumentError} on validation or backend failure
 */
export async function uploadBookingDocumentsB2b(
  orderId: string,
  files: File[],
  onProgress?: (progress: number) => void,
): Promise<UploadBookingDocumentsResult> {
  // Convert all files to base64, reporting progress
  const payloads: BookingDocumentFilePayload[] = []

  for (let i = 0; i < files.length; i++) {
    const file = files[i]
    try {
      const base64 = await fileToBase64(file)
      payloads.push({
        base64,
        fileName: file.name,
        mimeType: file.type,
      })
    } catch {
      throw new BookingDocumentError(
        "invalid-argument",
        `Datei "${file.name}" konnte nicht gelesen werden. Bitte erneut versuchen.`,
      )
    }

    if (onProgress) {
      onProgress((i + 1) / files.length)
    }
  }

  // Invoke the B2B callable
  try {
    return await callableWithAuthRetry<
      AttachBookingDocumentsRequest,
      AttachBookingDocumentsResponse
    >("attachBookingDocumentsB2b", { ticketId: orderId, files: payloads }, "mutation")
  } catch (error) {
    throw toBookingDocumentError(error)
  }
}
